import React, { useEffect, useState } from 'react';
import { getPlans } from '../../services/planService';
import { createCheckoutSession, redeemGiftCode } from '../../services/paymentService';

const formatPrice = (cents) => `$${(cents / 100).toFixed(2)}`;

const PricingCards = ({ onRedeemed }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [purchasingKey, setPurchasingKey] = useState(null);
  const [purchasesPaused, setPurchasesPaused] = useState(false);

  const [showRedeem, setShowRedeem] = useState(false);
  const [giftCodeInput, setGiftCodeInput] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState('');
  const [redeemSuccess, setRedeemSuccess] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getPlans();
        setPlans(data.plans);
        setPurchasesPaused(data.purchasesPaused);
      } catch (err) {
        setError('Failed to load pricing');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handlePurchase = async (planKey) => {
    setPurchasingKey(planKey);
    setError('');
    try {
      const data = await createCheckoutSession('plan', planKey);
      window.location.href = data.url; // redirect to Stripe Checkout
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start checkout');
      setPurchasingKey(null);
    }
  };

  const handleRedeem = async (e) => {
    e.preventDefault();
    if (!giftCodeInput.trim()) return;
    setRedeeming(true);
    setRedeemError('');
    setRedeemSuccess('');
    try {
      const data = await redeemGiftCode(giftCodeInput.trim());
      setRedeemSuccess(data.message);
      setGiftCodeInput('');
      if (onRedeemed) setTimeout(onRedeemed, 1200);
    } catch (err) {
      setRedeemError(err.response?.data?.message || 'Failed to redeem code');
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) return <p style={styles.status}>Loading plans...</p>;

  if (purchasesPaused) {
    return (
      <div style={styles.container}>
        <h2 style={styles.title}>🔒 New Signups Temporarily Unavailable</h2>
        <p style={styles.pausedText}>
          We're not accepting new cookbooks right now — please check back soon!
        </p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>🔒 Choose a Plan to access your new cookbook to view/add/print recipes and start inviting members to GrubClub.</h2>
      <p style={styles.oneTimeBadge}>
        💳 One-time purchase — never a subscription, never a recurring charge
      </p>
      <p style={styles.subtitle}>You can upgrade or buy more AI recipe scans anytime.</p>
      {error && <p style={styles.error}>{error}</p>}
      <div style={styles.grid}>
        {plans.map((plan) => (
          <div key={plan.plan_key} style={styles.card}>
            <h3 style={styles.planName}>{plan.name}</h3>
            <p style={styles.price}>{formatPrice(plan.price_cents)}</p>
            <p style={styles.oneTimeLabel}>one-time</p>
            <p style={styles.planDetail}>Up to {plan.max_members} members</p>
            <p style={styles.planDetail}>{plan.included_scans} AI recipe scans included</p>
            <button
              onClick={() => handlePurchase(plan.plan_key)}
              style={styles.buyBtn}
              disabled={purchasingKey === plan.plan_key}
            >
              {purchasingKey === plan.plan_key ? 'Redirecting...' : 'Purchase'}
            </button>
          </div>
        ))}
      </div>

      <div style={styles.redeemSection}>
        {!showRedeem ? (
          <>
            <p style={styles.redeemHeading}>Already have a gift code?</p>
            <button onClick={() => setShowRedeem(true)} style={styles.redeemToggle}>
              🎁 Redeem It Here
            </button>
          </>
        ) : (
          <form onSubmit={handleRedeem} style={styles.redeemForm}>
            <input
              type="text"
              placeholder="COOK-XXXX-XXXX"
              value={giftCodeInput}
              onChange={(e) => setGiftCodeInput(e.target.value.toUpperCase())}
              style={styles.redeemInput}
              disabled={redeeming}
            />
            <button type="submit" style={styles.redeemBtn} disabled={redeeming}>
              {redeeming ? 'Redeeming...' : 'Redeem'}
            </button>
          </form>
        )}
        {redeemError && <p style={styles.error}>{redeemError}</p>}
        {redeemSuccess && <p style={styles.redeemSuccessMsg}>{redeemSuccess}</p>}
      </div>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'white',
    padding: '32px',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    marginBottom: '24px',
    textAlign: 'center',
  },
  title: { color: '#e67e22', fontSize: '18px', lineHeight: 1.4, marginBottom: '12px' },
  oneTimeBadge: {
    display: 'inline-block',
    backgroundColor: '#fdf0e3',
    color: '#a05a1a',
    fontSize: '13px',
    fontWeight: '600',
    padding: '8px 16px',
    borderRadius: '20px',
    marginBottom: '10px',
  },
  subtitle: { color: '#888', fontSize: '13px', marginBottom: '24px' },
  oneTimeLabel: {
    color: '#27ae60',
    fontSize: '11px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '-4px 0 8px 0',
  },
  grid: { display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' },
  card: {
    border: '1px solid #eee',
    borderRadius: '10px',
    padding: '24px',
    width: '200px',
  },
  planName: { color: '#333', fontSize: '16px', marginBottom: '8px' },
  price: { color: '#e67e22', fontSize: '28px', fontWeight: '700', margin: '8px 0' },
  planDetail: { color: '#888', fontSize: '13px', marginBottom: '6px' },
  buyBtn: {
    width: '100%',
    padding: '10px',
    marginTop: '10px',
    backgroundColor: '#e67e22',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  status: { textAlign: 'center', color: '#999' },
  pausedText: { color: '#888', fontSize: '14px' },
  error: { color: '#e74c3c', fontSize: '14px', marginBottom: '16px' },
  redeemSection: {
    marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #f0f0f0',
    backgroundColor: '#fdf0e3', borderRadius: '10px', padding: '18px',
  },
  redeemToggle: {
    backgroundColor: 'white', border: '1px solid #e67e22', color: '#e67e22',
    fontSize: '14px', fontWeight: '600', cursor: 'pointer', padding: '10px 22px', borderRadius: '8px',
  },
  redeemHeading: { color: '#a05a1a', fontSize: '14px', fontWeight: '600', marginBottom: '10px' },
  redeemForm: { display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' },
  redeemInput: {
    padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px',
    textAlign: 'center', letterSpacing: '1px', width: '180px',
  },
  redeemBtn: {
    padding: '10px 20px', backgroundColor: '#e67e22', color: 'white', border: 'none',
    borderRadius: '8px', fontWeight: '600', cursor: 'pointer',
  },
  redeemSuccessMsg: { color: '#27ae60', fontSize: '13px', marginTop: '10px', fontWeight: '600' },
};

export default PricingCards;
